import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { sendChatbotMessage } from "../../services/post";
import { useConfig } from "../ConfigContext";
import { useClientAccessContext } from "../ClientAccessContext";

export enum ChatPhase {
	SELECTING = "selecting",
	ASKING = "asking",
	LOADING = "loading",
}

export enum ChatMessageAuthor {
	BOT = "bot",
	USER = "user",
}

export interface ChatMessage {
	author: ChatMessageAuthor;
	text: string;
	timestamp: number;
	isMarkdown?: boolean;
	isError?: boolean;
}

export interface ChatSelectedDocument {
	id: number;
	title: string;
}

interface IChatProvider {
	isChatOpen: boolean;
	phase: ChatPhase;
	isBotTyping: boolean;
	messages: ChatMessage[];
	selectedDocuments: ChatSelectedDocument[];
	isSelectionActive: boolean;
	toggleChat: () => void;
	closeChat: () => void;
	isDocumentSelected: (documentId: number) => boolean;
	toggleDocumentSelection: (document: ChatSelectedDocument) => void;
	removeDocumentFromSelection: (documentId: number) => void;
	confirmSelection: () => void;
	sendQuestion: (question: string) => void;
	resetConversation: () => void;
}

interface ChatContextProps {
	children?: React.ReactNode;
}

const WELCOME_MESSAGE =
	"Hi! Select the documents you want to ask about, then press Confirm.";

const ERROR_MESSAGE = "Something went wrong. Please try again.";

const BOT_TYPING_DELAY_MS = 500;

const ChatContext = createContext({} as IChatProvider);

const ChatProvider: React.FunctionComponent<ChatContextProps> = ({
	children,
}) => {
	const { selectedTopic } = useConfig();
	const { selectedClient } = useClientAccessContext();
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [phase, setPhase] = useState<ChatPhase>(ChatPhase.SELECTING);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [selectedDocuments, setSelectedDocuments] = useState<
		ChatSelectedDocument[]
	>([]);
	const [isBotTyping, setIsBotTyping] = useState(false);
	const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const sessionIdRef = useRef(crypto.randomUUID());

	const addMessage = (message: Omit<ChatMessage, "timestamp">) => {
		setMessages((prev) => [...prev, { ...message, timestamp: Date.now() }]);
	};

	const addBotMessageDelayed = (
		message: Omit<ChatMessage, "timestamp" | "author">,
		onShown?: () => void,
	) => {
		if (typingTimeoutRef.current) return;
		setIsBotTyping(true);
		typingTimeoutRef.current = setTimeout(() => {
			typingTimeoutRef.current = null;
			setIsBotTyping(false);
			addMessage({ ...message, author: ChatMessageAuthor.BOT });
			onShown?.();
		}, BOT_TYPING_DELAY_MS);
	};

	const toggleChat = () => {
		if (!isChatOpen && !messages.length) {
			addBotMessageDelayed({ text: WELCOME_MESSAGE });
		}
		setIsChatOpen((prev) => !prev);
	};

	const closeChat = () => setIsChatOpen(false);

	const isDocumentSelected = (documentId: number) =>
		selectedDocuments.some((d) => d.id === documentId);

	const toggleDocumentSelection = (document: ChatSelectedDocument) => {
		setSelectedDocuments((prev) => {
			if (prev.some((d) => d.id === document.id)) {
				return prev.filter((d) => d.id !== document.id);
			}
			return [...prev, document];
		});
	};

	const removeDocumentFromSelection = (documentId: number) => {
		setSelectedDocuments((prev) => prev.filter((d) => d.id !== documentId));
	};

	const confirmSelection = () => {
		if (!selectedDocuments.length) return;
		const documentList = selectedDocuments
			.map((d) => `- ${d.title}`)
			.join("\n");
		addBotMessageDelayed(
			{
				text: `**Selected documents:**\n${documentList}\n\nWhat would you like to know?`,
				isMarkdown: true,
			},
			() => setPhase(ChatPhase.ASKING),
		);
	};

	const sendQuestion = async (question: string) => {
		const trimmedQuestion = question.trim();
		if (!trimmedQuestion || phase !== ChatPhase.ASKING) return;
		addMessage({ author: ChatMessageAuthor.USER, text: trimmedQuestion });
		setPhase(ChatPhase.LOADING);
		const sessionId = sessionIdRef.current;
		const response = await sendChatbotMessage({
			message: trimmedQuestion,
			documentIds: selectedDocuments.map((d) => d.id),
			sessionId,
		});
		if (sessionId !== sessionIdRef.current) return;
		if (!response?.isSuccess) {
			addMessage({
				author: ChatMessageAuthor.BOT,
				text: response?.errorMessages?.[0] || ERROR_MESSAGE,
				isError: true,
			});
			setPhase(ChatPhase.ASKING);
			return;
		}
		addMessage({
			author: ChatMessageAuthor.BOT,
			text: response.result.message,
			isMarkdown: true,
		});
		setPhase(ChatPhase.ASKING);
	};

	const resetConversation = (reopenWelcome = isChatOpen) => {
		sessionIdRef.current = crypto.randomUUID();
		if (typingTimeoutRef.current) {
			clearTimeout(typingTimeoutRef.current);
			typingTimeoutRef.current = null;
		}
		setIsBotTyping(false);
		setMessages([]);
		setSelectedDocuments([]);
		setPhase(ChatPhase.SELECTING);
		if (reopenWelcome) {
			addBotMessageDelayed({ text: WELCOME_MESSAGE });
		}
	};

	useEffect(() => {
		resetConversation();
	}, [selectedClient?.clientId, selectedTopic?.id]);

	const chatContextValue: IChatProvider = {
		isChatOpen,
		phase,
		isBotTyping,
		messages,
		selectedDocuments,
		isSelectionActive: isChatOpen && phase === ChatPhase.SELECTING,
		toggleChat,
		closeChat,
		isDocumentSelected,
		toggleDocumentSelection,
		removeDocumentFromSelection,
		confirmSelection,
		sendQuestion,
		resetConversation,
	};

	return (
		<ChatContext.Provider value={chatContextValue}>
			{children}
		</ChatContext.Provider>
	);
};

const useChat = () => {
	const context = useContext(ChatContext);
	if (context === undefined) {
		throw new Error("useChat must be used within a ChatProvider");
	}
	return context;
};

export { ChatProvider, useChat };
